// Single source of truth for the Meta Ads data-source vocabulary, mirroring the
// server's RESOURCE_FIELD_META / METRIC_META / ACTION_TYPE_METRICS in
// server/services/cognitive-flows/metaAdsReportService.js.
//
// Both Meta editors read from here — components/nodes/AdsReadConfig.js and
// pages/CognitiveFlowsDataSourcesPage.jsx. They each carried a private copy before
// this module existed and had already drifted: the page offered two metrics the node
// didn't, and a wider `creative` metric list. Same reason adsFilterConfig.js exists;
// keep new Meta vocabulary here rather than in either host.
//
// The rule that matters: a label only belongs in these lists if the SERVER can resolve
// it to a column. A label with no server mapping renders as a checkbox that silently
// produces nothing — see the note on Cost per Result / Page Likes below.

export const META_ADS_REPORT_TYPES = [
  // Ordered by the Meta hierarchy, widest first: an account report is one row of
  // account-wide totals (level=account on the same /insights call).
  { value: 'account',         label: 'Account' },
  { value: 'campaign',        label: 'Campaign' },
  { value: 'adset',           label: 'Ad Set' },
  { value: 'adset_targeting', label: 'Ad Set Targeting' },
  { value: 'ad',              label: 'Ad' },
  { value: 'creative',        label: 'Creative' },
  { value: 'ad_enhancements', label: 'Ad Enhancements' },
  { value: 'adset_advantage', label: 'Ad Set Advantage+' },
  // Not part of the hierarchy: the account's change log, off the /activities
  // edge. Meta's counterpart to the Google "Change Logs" report.
  { value: 'change_history',  label: 'Change History' },
];

// Budgets and the per-level status labels are ENTITY fields server-side: they don't come
// from insights, they're joined from the /campaigns | /adsets | /ads edge. Budgets are
// only offered where one can actually be set — an ad has none.
//
// Bare 'Status' is RETIRED from the insights reports: sitting next to 'Campaign Status'
// and 'Ad Set Status' it never said whose status it was. Each report now names its own
// level explicitly. The swap is in place, because array position is the default column
// order. The server still maps 'Status' (ENTITY_FIELD_META) so sources saved before this
// keep emitting their column — same retire-label pattern the Google reports used.
// The two fixed-column status reports below keep 'Status': it is literally the header
// their fetchers emit, and renaming it would move a live column.
export const META_ADS_RESOURCE_FIELDS = {
  account:  ['Account ID', 'Account Name', 'Currency'],
  campaign: ['Campaign ID', 'Campaign Name', 'Objective', 'Campaign Status', 'Daily Budget', 'Lifetime Budget'],
  adset:    ['Campaign ID', 'Campaign Name', 'Ad Set ID', 'Ad Set Name', 'Optimization Goal', 'Ad Set Status', 'Campaign Status', 'Daily Budget', 'Lifetime Budget'],
  ad:       ['Campaign ID', 'Campaign Name', 'Ad Set ID', 'Ad Set Name', 'Ad ID', 'Ad Name', 'Ad Status', 'Campaign Status', 'Ad Set Status'],
  // Creative report — fixed output columns from the /ads edge with the creative
  // expanded (server buildCreativeRows owns this contract).
  creative: [
    'Campaign ID', 'Campaign Name', 'Ad Set ID', 'Ad Set Name', 'Ad ID', 'Ad Name', 'Status',
    'Creative ID', 'Creative Name', 'Creative Type', 'Headline', 'Description', 'Primary Text',
    'Call To Action', 'Landing Page URL', 'Image URL', 'Thumbnail URL', 'Instagram Permalink',
    'Ad Creative URL',
  ],
  // Ad Set Targeting report — fixed output columns from the /adsets edge with
  // targeting expanded (server buildAdsetTargetingRows owns this contract).
  adset_targeting: [
    'Campaign ID', 'Campaign Name', 'Ad Set ID', 'Ad Set Name', 'Status', 'Optimization Goal',
    'Countries', 'Regions', 'Cities', 'Excluded Locations', 'Age Min', 'Age Max', 'Genders',
    'Interests', 'Behaviors', 'Custom Audiences', 'Excluded Custom Audiences', 'Excluded Interests',
    'Publisher Platforms', 'Facebook Positions', 'Instagram Positions', 'Device Platforms',
    'Advantage+ Audience', 'Age Advantage+', 'Gender Advantage+', 'Geo Advantage+',
    'Advantage Detailed Targeting', 'Advantage Lookalike',
    'Advantage Custom Audience', 'Advantage+ Placements', 'Raw Targeting',
  ],
  // Status reports — the full column vocabulary. The server builds every column
  // and then projects onto the user's selection (projectStatusReportColumns in
  // metaAdsReportService.js); selecting nothing keeps every column.
  // Enhancement labels follow Meta's Ads Manager wording (product-supplied mapping,
  // 2026-08); byte-identical to AD_ENHANCEMENT_FEATURES in the server report service.
  // 'Add Text Overlay' is ONE column fed by both the image_templates and legacy
  // add_text_overlay api keys — the old separate 'Image Templates' column is gone.
  ad_enhancements: [
    'Campaign ID', 'Campaign Name', 'Ad Set ID', 'Ad Set Name', 'Ad ID', 'Ad Name', 'Status',
    'Standard Enhancements', 'Adapt to Placement', 'Add Text Overlay', 'Add details to ad layout',
    'Creative Stickers', 'Dynamic Description', 'Enhance CTA', 'Image Animation',
    'Generate Backgrounds', 'Brightness & Contrast', 'Translate Image Text', 'Visual Touch ups',
    'Expand Image', 'Relevant Comments', 'Highlight carousel card', 'Product Video', 'Music',
    'Flexible Media', 'Add product tags', 'Profile end card', 'Reveal Details Over Time',
    'Show spotlights', 'Show Summary', 'Site Extensions', 'Text Improvements', 'Translate Text',
    'Translate Voiceover', 'Visual Touch-ups', 'Add video effects', 'Video Highlights',
    'Expand Video', 'Other Enhancements',
  ],
  adset_advantage: [
    'Campaign ID', 'Campaign Name', 'Ad Set ID', 'Ad Set Name', 'Status', 'Optimization Goal',
    'Advantage+ Audience', 'Age Advantage+', 'Gender Advantage+', 'Geo Advantage+',
    'Advantage Detailed Targeting', 'Advantage Lookalike',
    'Advantage Custom Audience', 'Advantage+ Placements', 'Advantage Campaign Budget',
    'Advantage+ Shopping Campaign',
  ],
  // Change History — fixed output columns from the /activities edge (server
  // buildChangeHistoryRows owns this contract; byte-identical to its
  // META_CHANGE_HISTORY_COLUMNS). There is no 'User Email' counterpart to the
  // Google report: Meta identifies who made a change by name, not email.
  change_history: [
    'Change Date/Time', 'Change Date/Time (Account TZ)', 'Event Type', 'Change Description',
    'Actor ID', 'Actor Name', 'Object Type', 'Object ID', 'Object Name',
    'Changed Fields', 'Old Value', 'New Value', 'Application', 'Raw Change Data',
  ],
};

// Every entry here resolves server-side, either to an insights field (METRIC_META) or
// to an `actions` action_type (ACTION_TYPE_METRICS).
//
// 'Cost per Result' and 'Page Likes' are deliberately ABSENT. The Data Sources page
// offered both with no server mapping of any kind, so ticking them produced no column
// and no error. They are held out rather than copied into a second editor; they come
// back once the insights field name (cost_per_result) and action type (like) are
// verified against a live account.
export const META_ADS_METRICS = [
  'Impressions', 'Reach', 'Frequency', 'Clicks', 'Link Clicks',
  'CTR (All)', 'CTR (Link)', 'CPC', 'CPM', 'Spend',
  'Conversions', 'Cost per Conversion', 'ROAS',
  'Video Views', '3-sec Video Views', 'ThruPlay Views',
  'Post Engagement', 'Comments', 'Shares',
];

export const META_ADS_METRICS_BY_REPORT = {
  // Every insights metric is valid at level=account — it is the same call, aggregated.
  account:  META_ADS_METRICS,
  campaign: META_ADS_METRICS,
  adset:    META_ADS_METRICS,
  ad:       META_ADS_METRICS,
  // Entity snapshots — all four mirror META_STATUS_REPORT_METRICS in server
  // metaAdsReportService.js: the five plain scalar insights fields their
  // optional metrics merge can read. (creative previously offered the wider
  // ad-level list, but as a real snapshot report it merges through the same
  // path as the other three now.)
  creative:        ['Impressions', 'Clicks', 'Spend', 'CTR (All)', 'CPC'],
  adset_targeting: ['Impressions', 'Clicks', 'Spend', 'CTR (All)', 'CPC'],
  ad_enhancements: ['Impressions', 'Clicks', 'Spend', 'CTR (All)', 'CPC'],
  adset_advantage: ['Impressions', 'Clicks', 'Spend', 'CTR (All)', 'CPC'],
  // The change log has no insights call behind it at all. This entry is NOT
  // optional: metaMetricsFor falls back to the FULL metric list for a report
  // type it doesn't know, which would offer a complete metrics grid that
  // silently produces no columns.
  change_history: [],
};

// Offered breakdowns. 'City' and 'Conversion Destination' were in the page's list but
// have no dependable Meta insights breakdown behind them (Meta's documented delivery
// geography is country / region / dma), so they are held out for the same reason as the
// dead metrics above.
export const META_ADS_BREAKDOWNS = [
  'Day', 'Week', 'Month',
  'Age', 'Gender', 'Age & Gender',
  'Country', 'Region', 'DMA Region',
  'Impression Device', 'Platform', 'Platform & Device',
  'Placement', 'Placement & Device',
  'Product ID',
];

// Report types with no insights call behind them — breakdowns don't apply.
export const META_NO_BREAKDOWN_REPORTS = new Set(['ad_enhancements', 'adset_advantage', 'creative', 'adset_targeting', 'change_history']);

// Attribution windows. '' is Meta's own default (the ad set's unified setting) and is
// what Ads Manager shows, so it leads the list. Single-select deliberately: every extra
// window would multiply the event and cost columns.
export const META_ATTRIBUTION_WINDOWS = [
  { value: '',          label: 'Default (ad set setting)' },
  { value: '1d_click',  label: '1-day click' },
  { value: '7d_click',  label: '7-day click' },
  { value: '28d_click', label: '28-day click' },
  { value: '1d_view',   label: '1-day view' },
  { value: '7d_view',   label: '7-day view' },
  { value: '28d_view',  label: '28-day view' },
  { value: 'dda',       label: 'Data-driven attribution' },
];

/** Resource fields offered for one Meta report type. */
export const metaResourceFieldsFor = (reportType) => META_ADS_RESOURCE_FIELDS[reportType] || [];

/** Metrics offered for one Meta report type. */
export const metaMetricsFor = (reportType) =>
  META_ADS_METRICS_BY_REPORT[reportType] || (reportType ? META_ADS_METRICS : []);

/** Breakdowns offered for one Meta report type ([] where they don't apply). */
export const metaBreakdownsFor = (reportType) =>
  (!reportType || META_NO_BREAKDOWN_REPORTS.has(reportType)) ? [] : META_ADS_BREAKDOWNS;